// Mirror of Shared/Calculators/AcCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "roomWidthFt", kind: "number", default: 12, min: 1, max: 100 },
  { key: "roomLengthFt", kind: "number", default: 14, min: 1, max: 100 },
  { key: "ceilingHeightFt", kind: "number", default: 8, min: 6, max: 16 },
  {
    key: "sun",
    kind: "select",
    defaultChoice: "normal",
    choices: [
      { value: "shaded", set: { sunFactor: 0.9 } },
      { value: "normal", set: { sunFactor: 1.0 } },
      { value: "sunny", set: { sunFactor: 1.1 } },
    ],
  },
  { key: "occupants", kind: "number", default: 2, min: 1, max: 20 },
  {
    key: "kitchen",
    kind: "select",
    defaultChoice: "no",
    choices: [
      { value: "no", set: { isKitchen: 0 } },
      { value: "yes", set: { isKitchen: 1 } },
    ],
  },
];

function compute(i: Record<string, number>): Record<string, number> {
  const w = F.get(i, "roomWidthFt");
  const l = F.get(i, "roomLengthFt");
  const h = F.get(i, "ceilingHeightFt");
  const sunFactor = F.get(i, "sunFactor");
  const occupants = F.get(i, "occupants");
  const isKitchen = F.get(i, "isKitchen");

  const area = w * l;
  const baseBtu = area * 25 * (h / 8);
  const adjusted = baseBtu * sunFactor;
  const occExtra = Math.max(0, occupants - 2) * 600;
  const kitchenExtra = isKitchen >= 1 ? 4000 : 0;
  const totalBtu = adjusted + occExtra + kitchenExtra;
  const recommendedBtu = F.ceilUnits(totalBtu / 1000) * 1000;
  const tons = totalBtu / 12000;

  return {
    area: F.round(area, 2),
    baseBtu: F.round(baseBtu, 2),
    totalBtu: F.round(totalBtu, 2),
    recommendedBtu,
    tons: F.round(tons, 2),
  };
}

function present(_i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  return [
    { label: "Room area", value: `${F.dec(F.get(r, "area"))} sq ft` },
    { label: "Cooling needed", value: `${F.intStr(F.get(r, "totalBtu"))} BTU` },
    { label: "Recommended unit", value: `${F.intStr(F.get(r, "recommendedBtu"))} BTU`, highlight: true },
    { label: "In tons", value: `${F.dec(F.get(r, "tons"))} ton`, sub: "rough — a room estimate, not a whole-home Manual J load" },
  ];
}

export const ac: CalcDef = { slug: "ac-btu-calculator", fields, compute, present };
